import classNames from 'classnames'
import { Link, navigate } from 'gatsby'
import React, {
  MouseEventHandler,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import useProducts from '../../hooks/useProducts'
import {
  ShoppingCartItemWithProduct,
  getItemsWithProducts,
  useShoppingCart,
} from '../../hooks/useShoppingCart'
import { LocaleContext } from '../../locale/i18n'
import { staticPagePaths } from '../../locale/pathing'
import { gaEvents } from '../../logic/ga4'
import { StaticPage } from '../../logic/staticPage'
import useOnClickOutside from '../../logic/useOnClickOutside'
import Button from '../Button'
import CartItemParameters from '../cart/CartItemParameters'
import IconCart from '../icons/IconCart'
import IconDelete from '../icons/IconDelete'

import './ShoppingCart.scss'

const ProductPreviewRow = ({
  product,
  parameters,
  id,
}: ShoppingCartItemWithProduct) => {
  const { locale } = useContext(LocaleContext)
  const removeItem = useShoppingCart(state => state.removeItem)
  const { t } = useContext(LocaleContext)
  return (
    <div className="shoppingCart__itemWrapper">
      <div className="shoppingCart__item">
        <Link
          to={`${staticPagePaths[StaticPage.PRODUCTS][locale]}${
            product.slug ?? ''
          }`}
        >
          {product.name}
        </Link>
        {parameters ? (
          <CartItemParameters
            className="text--xs"
            product={product}
            parameters={parameters}
          />
        ) : null}
      </div>
      <Button
        aria-label={t('cart.removeItem')}
        onClick={event => {
          event.stopPropagation()
          gaEvents.productRemovedFromCart(product)
          removeItem(id)
        }}
        variant="plain"
      >
        <IconDelete width="16" />
      </Button>
    </div>
  )
}

const Contents = ({
  onCheckoutClicked,
  items,
}: {
  onCheckoutClicked: MouseEventHandler
  items: ShoppingCartItemWithProduct[]
}) => {
  const { t, interpolate, locale } = useContext(LocaleContext)

  if (!items || items.length === 0) {
    return (
      <p className="shoppingCart__empty">
        {interpolate('nav.cart.empty', {
          link: s => (
            <Link to={staticPagePaths[StaticPage.PRODUCTS][locale]}>{s}</Link>
          ),
        })}
      </p>
    )
  }

  return (
    <div className="shoppingCart__content">
      <div className="shoppingCart__items">
        {items.map(item => (
          <ProductPreviewRow {...item} key={item.id} />
        ))}
      </div>
      <Button className="mx-4 mb-2 h-10!" onClick={onCheckoutClicked}>
        {t('productPage.productConfiguration.addedToCart.proceed')}
      </Button>
    </div>
  )
}

const ShoppingCart = () => {
  const { t, locale } = useContext(LocaleContext)
  const items = useShoppingCart(state => state.items)
  const products = useProducts()
  const cartItems = getItemsWithProducts(items, products)

  const [showCartContent, setShowCartContent] = useState(false)

  // Cannot access cartItems.length directly to achieve clean hydration with no indicator
  const [showCartIcon, setShowCartIcon] = useState(false)
  useEffect(() => {
    setShowCartIcon(cartItems.length > 0)
  }, [cartItems])

  const containerEl = useRef(null)
  useOnClickOutside(
    containerEl,
    () => containerEl.current && setShowCartContent(false),
  )

  if (!showCartIcon) {
    return null
  }

  return (
    <div
      ref={containerEl}
      className={classNames('shoppingCart', {
        'shoppingCart--hidden': !showCartIcon,
      })}
    >
      <button
        aria-label={t('nav.cart.ariaLabel')}
        className="navbar__icon shoppingCart__button"
        data-test-id="cart-status"
        onClick={() => setShowCartContent(!showCartContent)}
        type="button"
      >
        <IconCart />
        <span className="shoppingCart__indicator" />
      </button>

      {showCartContent ? (
        <div className="navbar__popup shoppingCart__popup">
          <Contents
            items={cartItems}
            onCheckoutClicked={() => {
              setShowCartContent(false)
              void navigate(staticPagePaths[StaticPage.CART][locale])
            }}
          />
        </div>
      ) : null}
    </div>
  )
}

export default ShoppingCart
